// src/pages/Home.tsx
import React, { useEffect, useRef, useContext } from "react";
import { Link, Navigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { AuthContext } from "../context/AuthContext";
import { getAppRole } from "../lib/appRole";
import "./Home.css";
import "./HomeHeroButtons.css";
import DjSearch from "../components/DjSearch";
import IndustryCarousel from "../components/IndustryCarousel";
import TypedPriceBox from "../components/demo/TypedPriceBox";
import GetPaidDemo from "../components/demo/GetPaidDemo";
import AppStoreBadge from "../components/AppStoreBadge";
import HomeGlobe, { PayoutPartners } from "../components/home/HomeGlobe";
import TopSongsCarousel from "../components/home/TopSongsCarousel";
import { trackOnce } from "../lib/analytics";
import { t } from "../i18n";
import { T } from "../i18n/T";

// The industry-leaders carousel and the "how it works" demo widgets (typed
// price box; step 4's debit card, GetPaidDemo) live in shared components.

const Home: React.FC = () => {
  const { isAuthenticated, loading } = useContext(AuthContext);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Growth funnel: the web marketing page is the landing (native devices
  // redirect below before this page ever shows — their landing is the tour).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) trackOnce("landing_viewed", "home", { screen: "landing" });
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const dots = Array.from({ length: 90 }, () => ({
      angle: Math.random() * Math.PI * 2,
      radius: 80 + Math.random() * 340,
      speed: (0.00015 + Math.random() * 0.00025) * (Math.random() > 0.5 ? 1 : -1),
      size: 0.8 + Math.random() * 1.4,
      opacity: 0.1 + Math.random() * 0.22,
      color: Math.random() > 0.55 ? "#24C961" : "#ffffff",
      yFlatten: 0.3 + Math.random() * 0.25,
    }));

    let raf: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      dots.forEach((dot) => {
        dot.angle += dot.speed;
        const x = cx + Math.cos(dot.angle) * dot.radius;
        const y = cy + Math.sin(dot.angle) * dot.radius * dot.yFlatten;
        ctx.beginPath();
        ctx.arc(x, y, dot.size, 0, Math.PI * 2);
        ctx.fillStyle = dot.color;
        ctx.globalAlpha = dot.opacity;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // Hero backdrop — reveal on scroll (image rises + fades in over the first ~350px)
  useEffect(() => {
    const REVEAL_RANGE = 220;
    const handleScroll = () => {
      const progress = Math.min(Math.max(window.scrollY / REVEAL_RANGE, 0), 1);
      document.documentElement.style.setProperty("--hero-bg-reveal", String(progress));
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.documentElement.style.removeProperty("--hero-bg-reveal");
    };
  }, []);

  // Logged-in talent shouldn't land on the marketing page — send them straight
  // to their requests. Wait for the auth bootstrap to finish first so anonymous
  // visitors still see the landing page without a flash-redirect.
  if (!loading && isAuthenticated) {
    return <Navigate to="/requests" replace />;
  }

  // Native app: a logged-out fresh install gets the app-style welcome tour
  // instead of this web marketing page. A device that already
  // picked "I'm a fan" on the role screen skips straight to fan mode (app
  // fan mode, locked r6 §4) — a DJ login above always wins over the
  // remembered role. Web is untouched.
  if (!loading && Capacitor.isNativePlatform()) {
    if (getAppRole() === "fan") {
      return <Navigate to="/fan" replace />;
    }
    return <Navigate to="/welcome" replace />;
  }

  return (
    <div className="home">

      {/* ── Hero ── */}
      <section className="hero">
        <canvas ref={canvasRef} className="hero-canvas" aria-hidden />
        <p className="hero-eyebrow">{t("home.welcome")}</p>
        <h1 className="hero-title">
          <span className="hero-shimmer"><T k="home.getPaidForSong" tags={{ 0: <br /> }} /></span>
        </h1>
        <p className="hero-subline">{t("home.acceptDeclineOrCounter")}</p>
        <div className="hero-cta">
          {/* DJ signup is THE hero action (founder: the fan button is
              gone — "Looking for someone? Search DJs →" beneath already IS
              the fan door). Login lives in the header. */}
          <div className="hero-cta-buttons">
            <a href="/register" className="btn btn-lg hero-login">{t("home.signUpAsA")}</a>
            {/* Appears on its own the day the App Store listing is live (lib/appStore). */}
            <AppStoreBadge />
          </div>
          <p className="hero-cta-note">
            <T k="home.noSubscriptionsJustMore" tags={{ 0: (c) => <span className="hero-cta-note-accent">{c}</span> }} />
          </p>
        </div>
      </section>

      {/* ── Trusted by Industry Leaders ── */}
      <section className="section section-gradient ambassadors-section">
        <h2 className="section-heading">
          <T k="home.trustedByIndustryLeaders" tags={{ 0: (c) => <span className="heading-accent">{c}</span> }} />
        </h2>
        <IndustryCarousel />
        <DjSearch entryTo="/fan" />
      </section>

      {/* ── Top Songs: the most requested songs drifting the other way, every card and "See all" → /fan/leaderboard ── */}
      <TopSongsCarousel />

      {/* ── How It Works ── */}
      <section className="how-it-works">
        <h2 className="how-it-works-heading">
          <span className="hero-shimmer">{t("home.howItWorks")}</span>
        </h2>
        <div className="how-step">
          <h3 className="how-step-title">{t("home.setYourRequestPrice")}</h3>
          <div className="how-price-block">
            <TypedPriceBox />
          </div>
        </div>
        <div className="how-step">
          <h3 className="how-step-title">{t("home.fansScanYourQr")}</h3>
          <img
            src="/dj-lucky-wallet-mockup.png"
            alt={t("home.djLuckySRequest")}
            className="how-step-img"
          />
        </div>
        <div className="how-step how-step-wider">
          <h3 className="how-step-title">{t("home.acceptDeclineOrCounter2")}</h3>
          <img
            src="/accept-decline-counteroffer.png"
            alt={t("home.acceptDeclineOrCounteroffer")}
            className="how-step-img how-step-img-wider"
          />
        </div>
        <div className="how-step how-step-wide">
          <h3 className="how-step-title">{t("home.getPaid")}</h3>
          <GetPaidDemo />
        </div>
      </section>

      {/* ── Borderless Earnings ── */}
      <section className="section section-gradient borderless-section">
        <h2 className="section-heading">
          <T k="home.borderlessEarnings" tags={{ 0: (c) => <span className="heading-accent">{c}</span> }} />
        </h2>
        <HomeGlobe />
        <p className="borderless-sub">{t("home.oneProfileGetPaid")}</p>
        <p className="borderless-tagline">{t("home.paymentsAvailableInCountries")}</p>
        <PayoutPartners label={t("home.securePayoutsPoweredByPartners")} />
      </section>

      {/* ── CTA ── */}
      <section className="section section-dark section-cta">
        <h2 className="section-heading section-heading-lg">
          <T k="home.joinDjsArtistsPerformers" tags={{ 0: <br />, 1: (c) => <span className="heading-accent">{c}</span> }} />
        </h2>
        <p className="section-text">
          {t("home.monetizeYourAudienceWith")}
        </p>
        <div className="cta-buttons">
          <a href="/register" className="btn btn-green-glow btn-lg">
            {t("home.startEarning")}
          </a>
          <Link to="/login" className="btn btn-outline-white btn-lg">
            {t("home.login")}
          </Link>
        </div>
        <p className="cta-trust-line">
          {t("home.noSetupFeesFast")}
        </p>
      </section>

      {/* ── Page-bottom footer. The Learn More button + page are retired
          (founder: "super outdated") — /learn-more now redirects home. ── */}
      <section className="section section-dark learn-more-bottom">
        <p className="home-footer-brand">{t("home.requestApp")}</p>
      </section>

    </div>
  );
};

export default Home;
